import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Modal,
    NativeSelect,
    Paper,
    Select,
    TextField,
} from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';

import { doorDashApi } from 'api/doorDash';
import { goPuffApi } from 'api/goPuff';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

// The table to show details
function BulkPackSlipsAndBOLsTable({ api, facilityID, getRows, lineHauls, wholesaleAccount }) {
    const [lineHaulName, setLineHaulName] = useState(null);
    const [deliveryDate, setDeliveryDate] = useState(dayjs().add(1, 'days').format('YYYY-MM-DD'));
    const [notification, setNotification] = useState({ text: null, level: null });
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState([]);
    const [dataIsReady, setDataIsReady] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const handleDateChange = (e) => {
        setDeliveryDate(e.target.value);
    };

    const reset = async () => {
        setDataLoaded([]);
        setLoading(false);
        setLineHaulName(null);
        setDataIsReady(false);
        setExpanded(false);
    };

    useEffect(() => {
        if (lineHaulName !== null && dataLoaded.length > 0) {
            setDataIsReady(true);
            setLoading(false);
            setExpanded(true);
        }
    }, [lineHaulName, dataLoaded]);

    return (
        <div>
            <Notifications options={notification} />
            <Paper style={{ padding: '30px' }}>
                <TextField
                    id="deliveryDate"
                    label="Delivery Date"
                    type="date"
                    value={deliveryDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    disabled={loading}
                    onChange={handleDateChange}
                    style={{ marginRight: '50px', padding: '10px', border: '1px solid gray', borderRadius: '10px' }}
                />
                <Button
                    variant="outlined"
                    disabled={loading}
                    style={{ background: '#4050B5', color: '#FFFFFF' }}
                    onClick={() => {
                        setDeliveryDate(deliveryDate);
                    }}
                >
                    Go
                </Button>
            </Paper>
            {lineHauls.map((lh) => (
                <Grid container>
                    <Grid item md={12} style={{ textAlign: 'left', padding: '10px' }}>
                        <Button
                            id={lh.lineHaulName}
                            disabled={loading}
                            variant="outlined"
                            style={{ background: '#4050B5', color: '#FFFFFF' }}
                            onClick={async () => {
                                if (expanded === true) {
                                    setExpanded(false);
                                    reset();
                                    return;
                                }
                                setExpanded(!expanded);
                                setDataIsReady(false);
                                setLoading(true);
                                setLineHaulName(lh.lineHaulName);
                                const response = await api.getOrdersByLineHauls(
                                    facilityID,
                                    lh.lineHaulName,
                                    deliveryDate,
                                );
                                if (response.status === false) {
                                    setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
                                    reset();
                                    return;
                                }

                                if (response.data.rows && response.data.rows.length === 0) {
                                    setNotification({
                                        text: `No orders for line haul ${lh.lineHaulName}`,
                                        level: 'success',
                                    });
                                    reset();
                                    return;
                                }

                                const lhData = response.data.rows;
                                setDataLoaded(lhData);
                            }}
                        >
                            {' '}
                            {expanded === true && lineHaulName === lh.lineHaulName ? '-' : '+'}{' '}
                        </Button>
                        <strong>
                            <span style={{ paddingLeft: '10px', paddingRight: '40px' }}> {lh.lineHaulName} </span>
                        </strong>{' '}
                        <Button
                            variant="outlined"
                            disabled={loading}
                            style={{ background: '#4050B5', color: '#FFFFFF', marginRight: '30px' }}
                            onClick={async () => {
                                setLoading(true);
                                setLineHaulName(lh.lineHaulName);

                                const res = await api.bulkDownloadPackSlips(facilityID, lh.lineHaulName, deliveryDate);
                                if (res && res.status === true) {
                                    setLoading(false);
                                    reset();
                                } else {
                                    setNotification({ text: `Failed to download PDF: ${res.msg}`, level: 'error' });
                                    reset();
                                }
                            }}
                        >
                            Bulk Pack Slips
                        </Button>{' '}
                        <Button
                            variant="outlined"
                            disabled={loading}
                            style={{ background: '#4050B5', color: '#FFFFFF' }}
                            onClick={async () => {
                                setLoading(true);
                                setLineHaulName(lh.lineHaulName);
                                const res = await api.bulkDownloadBOLs(facilityID, lh.lineHaulName, deliveryDate);

                                if (res && res.status === true) {
                                    setLoading(false);
                                    reset();
                                } else {
                                    setNotification({
                                        text: `Failed to download HTML: ${res.msg}`,
                                        level: 'error',
                                    });
                                    reset();
                                }
                            }}
                        >
                            Bulk BOLs
                        </Button>
                    </Grid>
                    {expanded === true && dataIsReady === true && lineHaulName === lh.lineHaulName && (
                        <Grid item md={12}>
                            <ViewLineHaulDetailsTable
                                data={dataLoaded}
                                wholesaleAccount={wholesaleAccount}
                                api={api}
                                getRows={getRows}
                            />
                        </Grid>
                    )}
                </Grid>
            ))}
        </div>
    );
}

// The table to show details
function ViewLineHaulDetailsTable({ api, data, getRows, wholesaleAccount }) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [selectedOrderID, setSelectedOrderID] = useState(0);
    const [markDeliveredModalOpen, setMarkDeliveredModalOpen] = useState(false);

    const history = useHistory();

    useEffect(() => {
        if (data.length > 0) {
            setRows(data);
        }
    }, [data]);

    const columns = [
        { accessor: 'shipmentID', Header: 'Shipment ID' },
        { accessor: 'orderNum', Header: 'Order Num' },
        { accessor: 'status', Header: 'Shipment Status' },
        { accessor: 'microFC', Header: 'Micro FC' },
        { accessor: 'poNum', Header: 'PO#' },
        { accessor: 'createdAt', Header: 'Created At' },
        { accessor: 'deliveryDate', Header: 'Delivery Date' },
        {
            accessor: 'packSlip',
            Header: 'Pack Slip',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Print"
                        onClick={async () => {
                            api.downloadPackSlip(row.original.shipmentID);
                        }}
                    >
                        Download
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'bol',
            Header: 'BOL',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Print"
                        onClick={async () => {
                            api.downloadBOL(row.original.shipmentID);
                        }}
                    >
                        Download
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'markDelivered',
            Header: 'Mark Delivered',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Delivered"
                        onClick={() => {
                            setSelectedOrderID(row.original.orderNum);
                            setMarkDeliveredModalOpen(true);
                        }}
                    >
                        Mark Delivered
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'issueCredit',
            Header: 'Credits',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Credit"
                        onClick={async () => {
                            history.push(`/gopuff/issue-po-credits?poNum=${row.original.poNum}`);
                        }}
                    >
                        Issue Credit
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <Notifications options={notification} />
            {markDeliveredModalOpen && (
                <MarkDeliveredModal
                    setModalOpen={setMarkDeliveredModalOpen}
                    selectedRow={selectedOrderID}
                    setNotification={setNotification}
                    getRows={getRows}
                    api={api}
                />
            )}
            <DataTable
                columns={columns}
                data={rows}
                expandable
                ExpansionComponent={<ViewDetailsTable wholesaleAccount={wholesaleAccount} api={api} />}
            />
        </div>
    );
}

// The table to show details
function ViewDetailsTable({ api, parentRow, wholesaleAccount }) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });

    // Load  table
    const getRows = async () => {
        const response = await api.getShipmentDetails(parentRow.original.shipmentID);
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(response.data.details.picks ? response.data.details.picks : []);
    };

    useEffect(() => {
        getRows();
    }, []);

    const columns = [
        { accessor: 'externalSku', Header: 'External SKU' },
        { accessor: 'thirdPartySku', Header: `${wholesaleAccount.companyName} SKU` },
        { accessor: 'name', Header: 'Product Name' },
        { accessor: 'qty', Header: 'Request Pick Qty' },
        { accessor: 'numPicked', Header: 'Actual Pick Qty' },
    ];

    return (
        <div>
            <Notifications options={notification} />
            <DataTable columns={columns} data={rows} />
        </div>
    );
}

function MarkDeliveredModal({ api, getRows, selectedRow, setModalOpen, setNotification }) {
    const [failedDelivery, setFailedDelivery] = useState(false);
    const [deliveryNotes, setDeliveryNotes] = useState('');
    const [formData, setFormData] = useState({
        deliveryDate: '',
        failedDelivery: false,
        deliveryNotes: '',
    });

    // Update formData when failedDelivery or deliveryNotes change
    useEffect(() => {
        setFormData((prevData) => ({
            ...prevData,
            failedDelivery,
            deliveryNotes,
        }));
    }, [failedDelivery, deliveryNotes]);

    const formFields = [
        {
            label: 'Delivery Date',
            inputElement: 'date',
            name: 'deliveryDate',
            gridValue: 6,
            inputProps: { required: true },
            onChange: (e) => {
                setFormData((prevData) => ({
                    ...prevData,
                    deliveryDate: e.target.value,
                }));
            },
        },
        {
            label: 'Failed Delivery',
            inputElement: 'checkbox',
            name: 'failedDelivery',
            gridValue: 6,
            initialValue: false,
            inputProps: {
                onChange: (e) => {
                    setFailedDelivery(e.target.checked);
                },
            },
        },
    ];

    // Add delivery notes field conditionally if failed delivery is checked
    if (failedDelivery) {
        formFields.push({
            label: 'Delivery Notes',
            inputElement: 'textField',
            name: 'deliveryNotes',
            gridValue: 12,
            inputProps: {
                required: true,
                placeholder: 'Please explain the reason for failed delivery',
                multiline: true,
                rows: 4,
                fullWidth: true,
                onChange: (e) => {
                    setDeliveryNotes(e.target.value);
                },
            },
        });
    }

    return (
        <Dialog
            open
            onClose={() => {
                setModalOpen(false);
            }}
        >
            <DialogTitle>Mark Order {selectedRow} Delivered</DialogTitle>
            <FormComponent
                formFields={formFields}
                onSubmit={async (formData, resetForm) => {
                    const sendParams = {
                        orderID: selectedRow,
                        deliveryDate: formData.deliveryDate,
                    };

                    // Add delivery notes to the API request if failed delivery is checked
                    if (failedDelivery) {
                        if (!deliveryNotes.trim()) {
                            setNotification({
                                text: 'Delivery notes are required for failed deliveries',
                                level: 'error',
                            });
                            return;
                        }
                        sendParams.deliveryNotes = deliveryNotes;
                        sendParams.failedDelivery = true;
                    }

                    const res = await api.markDelivered(sendParams);
                    if (!res.status) {
                        setNotification({ text: `Failed to mark delivered. ${res.msg}`, level: 'error' });
                        return;
                    }
                    setNotification({ text: 'Order marked delivered', level: 'success' });
                    if (getRows instanceof Function) {
                        getRows();
                    }
                    setModalOpen(false);
                }}
            />
            <DialogActions>
                <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

function CancelOrderModal({ api, getRows, poNum, setModalOpen, setNotification, shipmentID }) {
    return (
        <Dialog
            open
            onClose={() => {
                setModalOpen(false);
            }}
        >
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogContent>
                Are you sure you want to cancel purchase order {poNum}? This action will cancel all shipments associated
                with this purchase order and cannot be undone.
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setModalOpen(false)}>No</Button>
                <Button
                    onClick={async () => {
                        const res = await api.cancelOrder(poNum);
                        if (!res.status) {
                            setNotification({ text: `Failed to cancel order. ${res.msg}`, level: 'error' });
                            return;
                        }
                        setNotification({ text: 'Order cancelled successfully', level: 'success' });
                        if (getRows instanceof Function) {
                            getRows();
                        }
                        setModalOpen(false);
                    }}
                    color="secondary"
                >
                    Yes, Cancel Order
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ViewWholesaleOrders({ wholesaleAccount }) {
    const [rows, setRows] = useState([]);
    const [lineHauls, setLineHauls] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [markDeliveredModalOpen, setMarkDeliveredModalOpen] = useState(false);
    const [selectedOrderID, setSelectedOrderID] = useState(0);
    const [cancelOrderModalOpen, setCancelOrderModalOpen] = useState(false);
    const [selectedShipmentID, setSelectedShipmentID] = useState(null);
    const [selectedPoNum, setSelectedPoNum] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().subtract(14, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [loading, setLoading] = useState(false);
    const [api, setApi] = useState(wholesaleAccount.fbmAccountName === 'doordash' ? doorDashApi : goPuffApi);

    const history = useHistory();
    const user = useContext(UserContext);

    // Load  line hauls
    const getLineHauls = async () => {
        const response = await api.getLineHaulsWithOrders(user.getFacilityID());
        if (response.status === false) {
            setNotification({ text: `No line hauls found ${response.msg}`, level: 'error' });
            setLineHauls([]);
            return;
        }

        setLineHauls(response.data.rows ? response.data.rows : []);
    };

    // Load  table
    const getRows = async () => {
        setLoading(true);
        const response = await api.getOrdersWithinRange(user.getFacilityID(), startDate, endDate);
        setLoading(false);
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(response.data.rows ? response.data.rows : []);
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
    };

    const handleDateFiltersApply = () => {
        getRows();
    };

    useEffect(() => {
        if (wholesaleAccount.fbmAccountName === 'doordash') {
            setApi(doorDashApi);
        } else {
            setApi(goPuffApi);
        }
    }, [wholesaleAccount]);

    useEffect(() => {
        getRows();
        getLineHauls();
    }, [api]);

    const columns = [
        { accessor: 'shipmentID', Header: 'Shipment ID' },
        { accessor: 'orderNum', Header: 'Order Num' },
        { accessor: 'status', Header: 'Shipment Status' },
        { accessor: 'microFC', Header: 'Micro FC' },
        { accessor: 'poNum', Header: 'PO#' },
        { accessor: 'createdAt', Header: 'Created At' },
        { accessor: 'deliveryDate', Header: 'Delivery Date' },
        {
            accessor: 'packSlip',
            Header: 'Pack Slip',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Print"
                        onClick={async () => {
                            api.downloadPackSlip(row.original.shipmentID);
                        }}
                    >
                        Download
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'bol',
            Header: 'BOL',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Print"
                        onClick={async () => {
                            api.downloadBOL(row.original.shipmentID);
                        }}
                    >
                        Download
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'markDelivered',
            Header: 'Mark Delivered',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Delivered"
                        onClick={async () => {
                            setSelectedOrderID(row.original.orderNum);
                            setMarkDeliveredModalOpen(true);
                        }}
                    >
                        Mark Delivered
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'issueCredit',
            Header: 'Credits',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id="Credit"
                        onClick={async () => {
                            history.push(`/gopuff/issue-po-credits?poNum=${row.original.poNum}`);
                        }}
                    >
                        Issue Credit
                    </Button>
                </div>
            ),
        },
        {
            accessor: 'cancelOrder',
            Header: 'Cancel Order',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                const canCancel = row.original.status === 'LABEL_PRINTED' || row.original.status === 'LABEL_PURCHASED';
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            id="Cancel"
                            onClick={() => {
                                setSelectedShipmentID(row.original.shipmentID);
                                setSelectedPoNum(row.original.poNum);
                                setCancelOrderModalOpen(true);
                            }}
                            disabled={!canCancel}
                        >
                            Cancel
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <div>
                <Notifications options={notification} />
                <h1>{wholesaleAccount.companyName} Orders</h1>
                <Grid item md={6} style={{ textAlign: 'left', paddingLeft: '40px' }}>
                    <h2>Bulk Pack Slips and BOLs</h2>
                    <h2>Grouped by Line Haul</h2>
                </Grid>
                <BulkPackSlipsAndBOLsTable
                    lineHauls={lineHauls}
                    facilityID={user.getFacilityID()}
                    wholesaleAccount={wholesaleAccount}
                    api={api}
                    getRows={getRows}
                />
            </div>
            {markDeliveredModalOpen && (
                <MarkDeliveredModal
                    setModalOpen={setMarkDeliveredModalOpen}
                    selectedRow={selectedOrderID}
                    setNotification={setNotification}
                    getRows={getRows}
                    api={api}
                />
            )}

            <Notifications options={notification} />
            <Grid style={{ margin: '50px' }} />
            <div>
                <Grid item>
                    <TextField
                        id="reportStartDate"
                        label="Start Date"
                        type="date"
                        value={startDate}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        disabled={loading}
                        onChange={handleStartDateChange}
                        style={{ marginRight: '50px' }}
                    />

                    <TextField
                        id="reportEndDate"
                        label="End Date"
                        type="date"
                        value={endDate}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        disabled={loading}
                        onChange={handleEndDateChange}
                        style={{ marginRight: '50px' }}
                    />
                    <Button onClick={handleDateFiltersApply} color="secondary" disabled={loading}>
                        Apply
                    </Button>
                </Grid>
                <Grid item style={{ marginTop: 12 }}>
                    <Link to="/gopuff/order-upload">Upload</Link>
                </Grid>
                <h1>Individual Orders</h1>
                {cancelOrderModalOpen && (
                    <CancelOrderModal
                        shipmentID={selectedShipmentID}
                        poNum={selectedPoNum}
                        setModalOpen={setCancelOrderModalOpen}
                        setNotification={setNotification}
                        getRows={getRows}
                        api={api}
                    />
                )}
                <DataTable
                    columns={columns}
                    data={rows}
                    expandable
                    csvExport
                    csvExportFileName={`${wholesaleAccount.fbmAccountName}-orders`}
                    csvExportHeadersUseAccessor
                    ExpansionComponent={<ViewDetailsTable wholesaleAccount={wholesaleAccount} api={api} />}
                />
            </div>
        </div>
    );
}

export { ViewWholesaleOrders };
